import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Brain,
  ShieldAlert,
  Calendar,
  HelpCircle,
  CheckCircle2,
  Activity,
  ListChecks,
} from "lucide-react";

export default function EssentialGuideToOcdSymptomsAndCarePage() {
  const publishedDate = "2024-04-11";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/04/essential-guide-to-ocd-symptoms-and-care.jpg";

  const faqs = [
    {
      q: "What are the most common OCD symptoms?",
      a: "OCD is characterized by intrusive, distressing obsessions (unwanted thoughts, images, or urges) and compulsions (repetitive behaviors or mental acts performed to reduce anxiety). Common themes include contamination, harm, symmetry, and forbidden thoughts.",
    },
    {
      q: "How is OCD treated when therapy and medication aren't enough?",
      a: "When SSRIs and Exposure & Response Prevention (ERP) therapy fail, interventional options like TMS therapy—FDA-cleared for OCD—can modulate the overactive cortico-striatal circuits driving symptoms.",
    },
    {
      q: "Can TMS therapy treat OCD?",
      a: "Yes. TMS targets the supplementary motor cortex and anterior cingulate, regions hyperactive in OCD. Clinical studies show significant symptom reduction in treatment-resistant OCD patients.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Essential Guide to OCD Symptoms and Care | Neuro Wellness"
        description="A comprehensive guide to OCD symptoms, obsessions vs compulsions, evidence-based treatments including ERP, medication, and TMS therapy for resistant cases."
        canonical="/essential-guide-to-ocd-symptoms-and-care"
        keywords="OCD symptoms guide, obsessions compulsions, ERP therapy, TMS for OCD, OCD treatment Coral Springs FL"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/essential-guide-to-ocd-symptoms-and-care#webpage",
                name: "Essential Guide to OCD Symptoms and Care",
                url: "https://neurowellnesstms.com/essential-guide-to-ocd-symptoms-and-care",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Comprehensive clinical guide to Obsessive-Compulsive Disorder symptoms, diagnostic criteria, evidence-based therapies, and interventional TMS treatment.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/essential-guide-to-ocd-symptoms-and-care#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <ListChecks className="h-4 w-4" />
                    Clinical Condition Guide
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Apr 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  Essential Guide to{" "}
                  <span className="text-accent">OCD Symptoms and Care</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Understanding obsessions, compulsions, and the full spectrum
                  of evidence-based treatment options—from ERP therapy to TMS.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Care Consultation
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Check OCD Treatment Options
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS & ARTICLE */}
        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "OCD Symptoms & Care Guide",
                  href: "/essential-guide-to-ocd-symptoms-and-care",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                Obsessive-Compulsive Disorder (OCD) is a chronic neurological
                and psychiatric condition affecting roughly 1 in 40 adults. It
                traps individuals in a distressing cycle of intrusive thoughts
                and repetitive behaviors that can consume hours of each day.
              </p>

              <h2>Understanding Obsessions</h2>
              <p>
                <strong>Obsessions</strong> are persistent, unwanted thoughts,
                images, or urges that trigger intense anxiety. Common themes
                include fear of contamination, harm to self or others, unwanted
                sexual or religious thoughts, and a need for symmetry or
                exactness.
              </p>

              <h2>Understanding Compulsions</h2>
              <p>
                <strong>Compulsions</strong> are repetitive behaviors
                (handwashing, checking, counting) or mental acts (praying,
                silently repeating phrases) performed to neutralize the anxiety
                of obsessions. They provide temporary relief but reinforce the
                OCD cycle.
              </p>

              <h2>Evidence-Based Treatments</h2>
              <ul>
                <li>
                  <strong>Exposure & Response Prevention (ERP):</strong> The
                  gold-standard therapy, gradually exposing patients to feared
                  triggers while preventing compulsive responses.
                </li>
                <li>
                  <strong>SSRI Medications:</strong> High-dose SSRIs
                  (fluoxetine, sertraline, fluvoxamine) are first-line
                  pharmacotherapy.
                </li>
                <li>
                  <strong>TMS Therapy:</strong> FDA-cleared for
                  treatment-resistant OCD, targeting hyperactive
                  cortico-striatal circuits.
                </li>
              </ul>

              <h2>When Standard Treatment Isn't Enough</h2>
              <p>
                Up to 40% of OCD patients don't achieve adequate relief from
                SSRIs and ERP alone. For these individuals,{" "}
                <Link
                  to="/what-can-tms-therapy-treat"
                  className="text-primary hover:underline font-semibold"
                >
                  TMS therapy
                </Link>{" "}
                offers a non-invasive, medication-free option that directly
                modulates the brain circuits driving OCD symptoms.
              </p>
            </article>

            {/* FAQ SECTION */}
            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RELATED RESOURCES */}
            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related Condition Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link
                    to="/what-can-tms-therapy-treat"
                    className="hover:underline"
                  >
                    → What Can TMS Therapy Treat?
                  </Link>
                </li>
                <li>
                  <Link
                    to="/what-is-the-difference-between-anxiety-and-depression"
                    className="hover:underline"
                  >
                    → Anxiety vs Depression
                  </Link>
                </li>
                <li>
                  <Link
                    to="/the-benefits-of-psychotherapy-for-managing-anxiety-and-depression"
                    className="hover:underline"
                  >
                    → Benefits of Psychotherapy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-to-get-mental-health-help"
                    className="hover:underline"
                  >
                    → How to Get Mental Health Help
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
